import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { UserCircle, MapPin } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMPTY = { full_name: "", phone: "", street: "", city: "", state: "", country: "", postal_code: "" };

export function KycModal() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const openWith = useCallback((profile) => {
    setForm({ ...EMPTY, full_name: profile?.name || "", phone: profile?.phone || "" });
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!user || user.kind !== "customer") return;
    api.get("/me/profile").then((r) => {
      if (!r.data.profile_completed && !sessionStorage.getItem("kyc_skipped")) openWith(r.data);
    }).catch(() => {});
  }, [user, openWith]);

  useEffect(() => {
    const handler = () => {
      api.get("/me/profile").then((r) => openWith(r.data)).catch(() => openWith(user));
    };
    window.addEventListener("open-kyc", handler);
    return () => window.removeEventListener("open-kyc", handler);
  }, [openWith, user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const skip = () => { sessionStorage.setItem("kyc_skipped", "1"); setOpen(false); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { full_name, phone, ...address } = form;
      const { data } = await api.post("/me/kyc", { name: full_name, phone, address });
      setUser((u) => (u ? { ...u, name: data.name, profile_completed: true } : u));
      toast.success("Profile completed! You're all set.");
      setOpen(false);
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="kyc-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 text-[#FF6A1A]"><UserCircle size={22} /><span className="text-xs font-semibold uppercase tracking-wide">Complete your profile</span></div>
          <DialogTitle className="font-display text-2xl">Welcome to Qava 🎉</DialogTitle>
          <DialogDescription>Set up your profile and delivery address so your orders arrive at the right place.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-1">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Full name</Label><Input required value={form.full_name} onChange={set("full_name")} data-testid="kyc-name" /></div>
            <div><Label>Phone</Label><Input required value={form.phone} onChange={set("phone")} data-testid="kyc-phone" /></div>
          </div>
          <div className="flex items-center gap-2 text-sm font-semibold text-zinc-700 pt-1"><MapPin size={15} className="text-[#FF6A1A]" />Delivery address</div>
          <div><Label>Street address</Label><Input required value={form.street} onChange={set("street")} data-testid="kyc-street" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>City</Label><Input required value={form.city} onChange={set("city")} data-testid="kyc-city" /></div>
            <div><Label>State / Region</Label><Input required value={form.state} onChange={set("state")} data-testid="kyc-state" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Country</Label><Input required value={form.country} onChange={set("country")} data-testid="kyc-country" /></div>
            <div><Label>Postal code</Label><Input value={form.postal_code} onChange={set("postal_code")} data-testid="kyc-postal" /></div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" className="flex-1 rounded-full" onClick={skip} data-testid="kyc-skip">Skip for now</Button>
            <Button type="submit" disabled={saving} className="flex-1 rounded-full bg-[#FF6A1A] hover:bg-[#e85c10]" data-testid="kyc-submit">{saving ? "Saving…" : "Save & continue"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
